import React, { useState, useEffect, useCallback } from 'react';
import { Carousel, Spinner } from 'react-bootstrap';
import { Camera } from 'lucide-react';

const PhotoCarousel = ({ resortId, resortName, onPhotosDiscovered }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadedPhotos, setLoadedPhotos] = useState(new Set());

  // Your CDN base URL
  const PHOTO_BASE_URL = 'https://storage.googleapis.com/powderphotos/ski_photos';
  
  // Discover available photos
  const discoverPhotos = useCallback(async (resortId) => {
    const maxPhotosToCheck = 15;
    const photoUrls = [];
  
    for (let i = 1; i <= maxPhotosToCheck; i++) {
      const photoNumber = i.toString().padStart(2, '0');
      
      // Check both .jpg and .png
      for (const ext of ['jpg', 'png']) {
        const photoUrl = `${PHOTO_BASE_URL}/${resortId}/photo_${photoNumber}.${ext}`;
        photoUrls.push({
          id: `${i}-${ext}`,
          url: photoUrl,
          alt: `${resortName} - Photo ${i}`
        });
      }
    }
  
    const checkPromises = photoUrls.map(async (photo) => {
      try {
        const response = await fetch(photo.url, { 
          method: 'HEAD',
          cache: 'force-cache'
        });
        return response.ok ? photo : null;
      } catch {
        return null;
      }
    });
  
    const results = await Promise.allSettled(checkPromises);
    const validPhotos = results
      .filter(result => result.status === 'fulfilled' && result.value !== null)
      .map(result => result.value);
  
    return validPhotos;
  }, [PHOTO_BASE_URL, resortName]);

  useEffect(() => {
    const loadPhotos = async () => {
      if (!resortId) {
        setLoading(false);
        onPhotosDiscovered?.(false);
        return;
      }

      try {
        setLoading(true);
        const discoveredPhotos = await discoverPhotos(resortId);
        setPhotos(discoveredPhotos);
        onPhotosDiscovered?.(discoveredPhotos.length > 0);
      } catch (err) {
        console.error('Error loading photos:', err);
        onPhotosDiscovered?.(false);
      } finally {
        setLoading(false);
      }
    };

    loadPhotos();
  }, [resortId, discoverPhotos, onPhotosDiscovered]);

  const handlePhotoLoad = (photoId) => {
    setLoadedPhotos(prev => new Set([...prev, photoId]));
  };

  const handlePhotoError = (photoId) => {
    console.warn(`Failed to load photo ${photoId} for resort ${resortId}`);
    setPhotos(prev => prev.filter(photo => photo.id !== photoId));
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center bg-light" style={{ height: '400px' }}>
        <div className="text-center text-muted">
          <Spinner animation="border" className="mb-3" />
          <h6>Loading Photos...</h6>
        </div>
      </div>
    );
  }

  if (photos.length === 0) {
    return null;
  }

  const renderPhotoSlide = (photo) => (
    <div className="resort-photo-slide">
      {!loadedPhotos.has(photo.id) && (
        <div className="position-absolute top-50 start-50 translate-middle">
          <Spinner animation="border" size="sm" />
        </div>
      )}
      <img
        src={photo.url}
        alt=""
        aria-hidden="true"
        className="resort-photo-backdrop"
        onLoad={() => handlePhotoLoad(photo.id)}
        onError={() => handlePhotoError(photo.id)}
      />
      <img
        src={photo.url}
        alt={photo.alt}
        className="resort-photo-main"
        style={{
          opacity: loadedPhotos.has(photo.id) ? 1 : 0,
        }}
        loading="lazy"
      />
    </div>
  );

  if (photos.length === 1) {
    const photo = photos[0];
    return (
      <div style={{ position: 'relative' }}>
        {renderPhotoSlide(photo)}
        <div className="position-absolute bottom-0 end-0 bg-dark bg-opacity-75 text-white rounded-top-start p-2">
          <small>
            <Camera size={14} className="me-1" />
            1 Photo
          </small>
        </div>
      </div>
    );
  }

  return (
    <Carousel className="resort-carousel" interval={null}>
      {photos.map((photo) => (
        <Carousel.Item key={photo.id}>
          {renderPhotoSlide(photo)}
        </Carousel.Item>
      ))}
    </Carousel>
  );
};

export default PhotoCarousel;